/** @forge-header
 * @id          closeResidual
 * @role        Verify-and-retry close primitives | residual-from-IOC-response measure + sweep attempt cap
 * @pattern     pure
 * @purity      pure
 * @units       integer (contracts)
 * @money-path  yes
 * @upstream    kalshiApi/parseHelpers:fpToInt
 * @downstream  stores/accountStore:sweepCloseLeg | views/PositionTracker/EditPositionModal
 * @exports     residualOpenContracts:fn | classifyCloseResult:fn | MAX_CLOSE_ATTEMPTS:const
 * @test        closeResidual.test.ts
 */

import { fpToInt } from '../kalshiApi/parseHelpers';

/**
 * How many times to re-issue the marketable-IOC sweep when a close partial-fills.
 * The V2 close is an immediate-or-cancel marketable limit (type:'market' → IOC
 * at 0.99/0.01); against a thin opposite book it fills what it can and cancels
 * the rest, leaving residual exposure with NO error. We re-sweep the residual a
 * few times before giving up loudly. We deliberately do NOT fall back to a
 * resting GTC limit (that defeats a panic-close) and do NOT use FOK.
 */
export const MAX_CLOSE_ATTEMPTS = 3;

/**
 * Residual open contracts after an IOC close, read off the V2 flat-201 response.
 * The close is ALWAYS immediate_or_cancel (type:'market' → IOC), so the unfilled
 * remainder is CANCELLED, not left resting — Kalshi's `remaining_count` is 0 even
 * on a PARTIAL fill ("the final state after unfilled contracts are canceled").
 * So `requested − fill_count` is the authoritative unfilled measure; we take the
 * larger of it and remaining_count when both are present, so a residual from
 * EITHER field still triggers the retry/warning (never under-report a panic
 * close). 0 = confirmed flat. >0 = residual still open.
 *
 * WIRE SHAPE: V2 sends `fill_count`/`remaining_count` as FixedPointCount STRINGS
 * (e.g. "5.00") in the BARE field — no `_fp` suffix (verified: docs §5, and the
 * relay's own client.v2wire test mocks `remaining_count: '5'`). fpToInt's first
 * (string-parsing) slot must receive that value, so we pass `*_fp ?? bare` there;
 * the legacy number-only second slot would reject "5.00" → null → a false
 * "UNVERIFIED" on every real close. Numeric and explicit-`_fp` shapes still parse.
 *
 * Returns null when no fill_count is present: `remaining_count` is 0 on a partial
 * IOC too, so a lone `remaining_count:0` proves nothing — report UNVERIFIED rather
 * than a false flat. Only a POSITIVE lone remaining is trusted as a conservative
 * residual. An opaque response (neither field) is likewise null → caller must NOT
 * blind-retry.
 */
export function residualOpenContracts(result: unknown, requested: number): number | null {
  if (!result || typeof result !== 'object') return null;
  const r = result as Record<string, unknown>;
  const filled = fpToInt(r.fill_count_fp ?? r.fill_count, r.fill_count);
  const remaining = fpToInt(r.remaining_count_fp ?? r.remaining_count, r.remaining_count);
  if (filled !== null) {
    const byFill = Math.max(0, requested - filled);
    return remaining !== null ? Math.max(byFill, Math.max(0, remaining)) : byFill;
  }
  // No fill_count — the authoritative measure is gone. Trust only a POSITIVE lone
  // remaining (real residual); a lone remaining of 0 is zero-information on an IOC,
  // so report UNVERIFIED (null) instead of a false flat.
  if (remaining !== null && remaining > 0) return remaining;
  return null;
}

export interface CloseOutcome {
  /** false = a warning the user must act on (modal stays up). */
  ok: boolean;
  /** Auto-dismiss the modal ONLY on a fully-confirmed flat close. */
  autoDismiss: boolean;
  msg: string;
}

/**
 * Bucket a `sweepCloseLeg` residual into the close modal's user-facing outcome.
 * THREE distinct states — conflating `null` with `0` (the original bug) reported
 * a never-confirmed close as success and auto-dismissed the modal:
 *   - `null` → CLOSE UNVERIFIED: the IOC response carried no fill counts; the
 *     position may still be fully open. Warn, never auto-dismiss.
 *   - `> 0`  → CLOSE INCOMPLETE: a real residual survived every sweep attempt.
 *     Warn, never auto-dismiss.
 *   - `0`    → confirmed flat → success, auto-dismiss.
 * Pure so the bucketing is unit-pinned independently of the modal's wiring.
 */
export function classifyCloseResult(
  residual: number | null,
  ctx: { ticker: string; side: 'yes' | 'no'; quantity: number },
): CloseOutcome {
  if (residual === null) {
    return {
      ok: false,
      autoDismiss: false,
      msg:
        `⚠️ CLOSE UNVERIFIED — couldn't confirm the fill for ${ctx.ticker}. ` +
        `The exchange response carried no fill count; the position may still ` +
        `be open. Check /positions and close manually if needed.`,
    };
  }
  if (residual > 0) {
    return {
      ok: false,
      autoDismiss: false,
      msg:
        `⚠️ CLOSE INCOMPLETE — ${ctx.ticker} still has ${residual} contract(s) open after ` +
        `${MAX_CLOSE_ATTEMPTS} sweep attempts. The market close only partial-filled ` +
        `against a thin book; close the remainder manually.`,
    };
  }
  return {
    ok: true,
    autoDismiss: true,
    msg: `Closed ${ctx.quantity} ${ctx.side.toUpperCase()}.`,
  };
}
